import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box, Tabs, Tab, tabsClasses } from '@mui/material';

// Taller rows give a button in the row breathing room, and phones a bigger tap target.
const ROW_HEIGHT = { xs: 44, sm: 36 };
const ROW_HEIGHT_WITH_ACTION = 48;

// Scrollable sub-tab row (e.g. Trade History's exchanges), styled after PageTabs.
// Not MUI's scrollButtons="auto": its arrows eat 56px, so they stick after the
// row has room again and leave a blank gap when disabled.
const ReportsSubTabs = ({ options, value, onChange, ariaLabel, action }) => {
  const rootRef = useRef(null);
  const [overflows, setOverflows] = useState(false);
  const rowHeight = action ? ROW_HEIGHT_WITH_ACTION : ROW_HEIGHT;

  // Compared with the whole row (arrows included), so the result doesn't depend
  // on whether the arrows are showing.
  useLayoutEffect(() => {
    const root = rootRef.current;
    const list = root?.querySelector(`.${tabsClasses.flexContainer}`);
    if (!root || !list) return undefined;

    const update = () => setOverflows(list.scrollWidth > root.clientWidth);
    update();

    const observer = new ResizeObserver(update);
    observer.observe(root);
    Array.from(list.children).forEach((tab) => observer.observe(tab));
    return () => observer.disconnect();
  }, [options]);

  const tabs = (
    <Tabs
      ref={rootRef}
      value={value}
      onChange={(event, newValue) => onChange(newValue)}
      variant="scrollable"
      scrollButtons={overflows}
      allowScrollButtonsMobile={false}
      aria-label={ariaLabel}
      sx={{
        // minWidth 0 lets the tabs shrink and scroll instead of pushing the action out.
        flex: 1,
        minWidth: 0,
        minHeight: rowHeight,
        '& .MuiTabs-indicator': {
          height: 2,
          borderRadius: 1,
          backgroundColor: 'primary.main',
        },
        '& .MuiTabs-scrollButtons': { width: 28 },
        '& .MuiTabs-scrollButtons.Mui-disabled': { opacity: 0.3 },
      }}
    >
      {options.map((opt) => (
        <Tab
          key={opt.value}
          value={opt.value}
          label={opt.label}
          disableRipple
          sx={{
            minHeight: rowHeight,
            minWidth: 'auto',
            px: 1.5,
            textTransform: 'none',
            whiteSpace: 'nowrap',
            fontSize: '0.8125rem',
            fontWeight: 500,
            color: 'text.secondary',
            opacity: 1,
            transition: 'color 0.2s ease',
            '&:hover': { color: 'text.primary' },
            '&.Mui-selected': { color: 'primary.main', fontWeight: 600 },
            // disableRipple also removes the keyboard focus ring; restore one.
            '&.Mui-focusVisible': { bgcolor: 'action.focus' },
          }}
        />
      ))}
    </Tabs>
  );

  // Always render the wrapper so Tabs never remounts; the observer is bound to it.
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {tabs}
      {action && (
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{action}</Box>
      )}
    </Box>
  );
};

export default ReportsSubTabs;
